import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  AlertService,
  AudienceService,
  CampaignService,
  FunnelService,
  FunnelStepService,
  LoaderService,
  ProductService,
  UserService,
} from '../_services';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { FunnelNewComponent } from '../funnel';
import {
  FunnelInputTypeEnum,
  FunnelType,
  FunnelVariables,
  Image,
  OfferTypes,
  User,
  WelcomeMessageVariables,
  CustomerPortalSettingHints,
  FunnelStep,
  Funnel,
  Campaign,
  StepStaticDataImageType,
} from '../_models';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { FontFamilyFormats, FontSizes, initializeEditorStyles } from '../steps-library/step-util';
import { animate, state, style, transition, trigger } from '@angular/animations';
import * as tinymce from 'tinymce';

@Component({
  moduleId: module.id.toString(),
  templateUrl: './customer-portal-edit.component.html',
  styleUrls: ['../funnel/funnel.component.css', './customer-portal.component.scss'],
  animations: [
    trigger('fadeIn', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('500ms ease-in', style({ opacity: 1 }))]),
    ]),
  ],
  encapsulation: ViewEncapsulation.None,
})
export class CustomerPortalEditComponent extends FunnelNewComponent implements OnInit {
  availableSideMenuInputs: { id: FunnelInputTypeEnum; text: string }[] = [
    { id: FunnelInputTypeEnum.CustomerPortalHome, text: 'Home' },
    { id: FunnelInputTypeEnum.CustomerPortalOrders, text: 'Orders' },
    { id: FunnelInputTypeEnum.CustomerPortalSubscriptions, text: 'Subscriptions' },
    { id: FunnelInputTypeEnum.CustomerPortalPersonalInfo, text: 'Contact Information' },
  ];

  sideMenuInputSettings = {
    enableCheckAll: false,
  };

  protected settingsFields = {
    side_menu_inputs: [this.availableSideMenuInputs, Validators.required],
    is_link_internal: [false],
    link: [null],
  };

  protected defaultValues = {
    matched_verify_msg: 'Verify your identity',
    unmatched_verify_msg: 'Verify your identity',
    match_msg: "Let's find your account!",
    thank_you_msg: 'Thank you for visiting {campaign_name}!',
    matched_welcome_msg: 'Hello, {first_name}',
  };

  customerPortalHints = CustomerPortalSettingHints;
  welcomeMessageVariables: { value: string; text: string }[] = [];
  homeInputDeselected: boolean = false;
  matchedWelcomeMsgEditor = {
    height: 115,
    resize: false,
    fontsize_formats: FontSizes,
    font_formats: FontFamilyFormats,
    setup: (editor: tinymce.Editor) => {
      initializeEditorStyles(editor, '16px', "'Open Sans', sans-serif");
    },
  };
  user: User = null;
  bannerImages: any = {};
  imageSelectionField = null;
  protected jumpSteps = {};
  isDesktopImg: boolean = true;
  imageReady: boolean = true;
  campaign: Campaign = null;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected funnelService: FunnelService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected productService: ProductService,
    protected modalService: NgxSmartModalService,
    protected audienceService: AudienceService,
    protected userService: UserService,
    protected stepService: FunnelStepService,
    protected campaignService: CampaignService,
    protected loader: LoaderService
  ) {
    super(
      router,
      location,
      route,
      funnelService,
      alertService,
      formBuilder,
      productService,
      modalService,
      audienceService
    );
    this.isNew = false;
    this.objectName = 'Order Management Settings';
    this.title = '';
    this.funnelType = FunnelType.CustomerPortal;
  }

  ngOnInit() {
    super.ngOnInit();
    this.form.patchValue(this.defaultValues);
    this.welcomeMessageVariables = this.getWelcomeMessageVariables();

    this.userService.getCurrent().subscribe(
      (user: User) => {
        this.user = user;
      },
      (error) => {
        this.handleError(error);
      }
    );

    this.stepService
      .listAll({
        'funnel__offer_type!': OfferTypes.Legacy,
        'type__in!': [
          FunnelInputTypeEnum.EnterProductFunnel,
          FunnelInputTypeEnum.Confirmation,
          FunnelInputTypeEnum.InputActionStatus,
        ].join(','),
        is_template: false,
      })
      .subscribe(
        (steps: FunnelStep[]) => {
          if (steps) {
            let jumpSteps = {};

            steps.forEach((step: FunnelStep) => {
              jumpSteps[step.slug] = step.name;
            });
            this.jumpSteps = jumpSteps;
          }
        },
        (error) => {
          this.handleError(error);
        }
      );

    this.loader.show();
    this.campaign = this.campaignService.getCurrentCampaign();
    this.id = this.campaign.customer_portal_id;
    this.funnelService.get(this.id).subscribe(
      (funnel: Funnel) => {
        if (funnel) {
          this.funnel = funnel;
          this.form.patchValue(funnel);
          this.useMatchedWelcomeMessage = !!(funnel.matched_welcome_msg && funnel.matched_welcome_msg.length);

          if (funnel.side_menu_inputs) {
            let inputs = [];
            let labels = {};

            this.availableSideMenuInputs.forEach((input: { id: FunnelInputTypeEnum; text: string }) => {
              labels[input.id] = input.text;
            });

            funnel.side_menu_inputs.forEach((value) => {
              inputs.push({ id: value, text: labels[value] });
            });

            this.form.patchValue({ side_menu_inputs: inputs });
            delete funnel.side_menu_inputs; // delete this so patchValue works
          }

          if (funnel.banner_image_details) {
            this.bannerImages = {};
            this.bannerImages.image = funnel.banner_image_details.image || null;
            this.bannerImages.image_mobile = funnel.banner_image_details.image_mobile || null;
            this.form.patchValue({
              is_link_internal: funnel.banner_image_details.is_link_internal,
              link: funnel.banner_image_details.link,
            });
          }

          this.loader.hide();
        }
      },
      (error) => {
        this.loader.hide();
        this.handleError(error);
      }
    );
  }

  previewHome() {
    if (this.funnel && this.campaign) {
      this.loader.show();
      const data = this.getFormData();
      const sideMenuInputs = data['side_menu_inputs'] || [];
      const selectedInputsMap = {};
      sideMenuInputs.forEach((inputId: FunnelInputTypeEnum) => {
        selectedInputsMap[inputId] = true;
      });
      const homeStep = this.funnel.steps.find(
        (step) => step.type === FunnelInputTypeEnum.CustomerPortalHome);
      const filteredInputs = homeStep.inputs.filter((input) => input.type in selectedInputsMap);
      const stepData = Object.assign({}, homeStep, {
        static_data: data['banner_images_data'],
        inputs: filteredInputs,
        side_menu_inputs: sideMenuInputs,
        funnel: this.funnel.id,
      });
      this.stepService.setFunnelId(this.funnel.id);
      this.stepService.preview(this.campaign.id, stepData).subscribe(
        (data) => {
          window.open(data.toString(), '_blank');
          this.loader.hide();
        },
        (error) => {
          this.handleError(error);
          this.loader.hide();
        }
      );
    }
  }

  protected updateControlsForFunnelType() {
    const extraFields = Object.assign({}, this.defaultExtraFields, this.settingsFields);

    //add the extra fields
    Object.keys(extraFields).forEach((fieldName: string) => {
      let field = extraFields[fieldName];
      this.form.addControl(fieldName, new FormControl(field[0], field[1]));
    });

    this.updateTitle();
  }

  protected getFormData() {
    let data = { campaign: this.campaign.id };

    Object.entries(this.form.value).forEach(([fieldName, value]) => {
      if (fieldName in this.settingsFields) {
        if (fieldName === 'side_menu_inputs') {
          let inputs = [];

          if (value) {
            this.form.value.side_menu_inputs.forEach((input) => {
              inputs.push(input.id);
            });
          }

          data[fieldName] = inputs;
        } else {
          data[fieldName] = value;
        }
      } else {
        data[fieldName] = value;
      }
    });

    if (Object.keys(this.bannerImages).length > 0) {
      data['banner_images_data'] = {
        is_link_internal: data['is_link_internal'],
        link: data['link'],
        image_type: StepStaticDataImageType.Fixed,
      };
      data['banner_images_data']['image'] = this.bannerImages.image
        ? this.bannerImages.image.id
        : null;
      data['banner_images_data']['image_mobile'] = this.bannerImages.image_mobile
        ? this.bannerImages.image_mobile.id
        : null;
    }

    return data;
  }

  publish() {
    const funnelId = this.id || this.funnel.id;
    if (funnelId) {
      this.funnelService.publish(funnelId).subscribe(
        () => {
          this.loading = false;
          this.alertService.success('Order Management Settings Published Successfully.', true);
          this.navigate(['/customer-portals'], { replaceUrl: true });
        },
        (error) => {
          this.handleError(error);
          this.loading = false;
        }
      );
    }
  }

  protected onSaveComplete(data) {
    this.loading = false;
  }

  addMessageVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = event.target.value;
    const editorInstance = tinymce.get('matched_welcome_msg');
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }

  navigateBackToSettings() {
    this.router.navigate(['customer-portals']);
  }

  protected getWelcomeMessageVariables() {
    let items = [];

    Object.keys(WelcomeMessageVariables).forEach((key) => {
      items.push({ value: '{' + key + '}', text: FunnelVariables[key][0] });
    });

    return items;
  }

  onSideMenuInputChange(): void {
    const selectedValues = this.form.get('side_menu_inputs').value;
    this.homeInputDeselected = !selectedValues.some((item) => item.text === 'Home');
  }

  imageSelectStart(field: string) {
    this.imageSelectionField = field;
    this.modalService.getModal('imageDialog').open();
  }

  removeImage(field: string) {
    this.bannerImages[field] = null;
  }

  onImageSelected(image: Image) {
    this.modalService.getModal('imageDialog').close();

    // Set loading state
    this.imageReady = false;
    this.bannerImages[this.imageSelectionField] = image;
    this.imageSelectionField = null;

    // Simulate loading delay
    setTimeout(() => {
      this.imageReady = true;
    }, 1000);
  }

  updateBannerTab(isDesktopSelected: boolean) {
    this.isDesktopImg = isDesktopSelected;
  }

  isLinkInternal() {
    return this.form.value.is_link_internal;
  }

  onLinkTypeChange(isInternal: boolean): void {
    if (!isInternal) {
      this.form.patchValue({ link: null });
    }
  }

  get jumpStepKeys() {
    return Object.keys(this.jumpSteps);
  }
}
